import React, { useState } from 'react';
import { X, Phone, Calendar, CreditCard, DollarSign, User, Users, CheckCircle, XCircle, Clock, RefreshCw, AlertCircle, TrendingUp, ArrowRight, MapPin, Trash2, Shield } from 'lucide-react';
import Modal from '../ui/Modal';
import BookingActivityTimeline from './BookingActivityTimeline';
import { useAuth } from '../../contexts/AuthContext';
import { OneTimeBooking, RecurringBooking } from '../../types/bookings';
import {
  formatDate,
  formatTime,
  formatDateTimeString,
  formatCurrency,
  formatDurationFromTimes,
  getStatusColor,
  getPaymentStatusColor,
  canCheckIn,
  canReschedule,
  canCancel
} from '../../utils/bookingHelpers';

interface BookingDetailsModalProps {
  booking: OneTimeBooking | RecurringBooking | null;
  isOpen: boolean;
  onClose: () => void;
  onCheckIn?: (bookingId: number) => Promise<void>;
  onReschedule?: (bookingId: number) => void;
  onCancel?: (bookingId: number) => void;
  onDelete?: (bookingId: number) => Promise<void>;
  // Capability gates (staff-access): when false, the relevant action button
  // renders greyed-out with a tooltip instead of being usable. Default true so
  // existing callers (owner flows) are unaffected.
  allowCheckIn?: boolean;
  allowReschedule?: boolean;
  allowCancel?: boolean;
  allowDelete?: boolean;
}

export default function BookingDetailsModal({
  booking,
  isOpen,
  onClose,
  onCheckIn,
  onReschedule,
  onCancel,
  onDelete,
  allowCheckIn = true,
  allowReschedule = true,
  allowCancel = true,
  allowDelete = true,
}: BookingDetailsModalProps) {
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { user: currentUser } = useAuth();

  // Early return if no booking - must be after all hooks
  if (!booking) {
    return null;
  }

  const handleCheckIn = async () => {
    if (!onCheckIn) return;

    setIsCheckingIn(true);
    try {
      await onCheckIn(booking.id);
      onClose();
    } catch (error) {
      console.error('Check-in failed:', error);
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setIsDeleting(true);
    try {
      await onDelete(booking.id);
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Type guard to check if booking is recurring
  const isRecurring = 'frequency' in booking;

  // Extract data based on booking type
  const bookingDate = isRecurring
    ? ((booking as RecurringBooking).next_session_date || (booking as RecurringBooking).start_date)
    : (booking as OneTimeBooking).booking_date;

  const startTime = booking.start_time;
  const endTime = booking.end_time;

  const statusColor = getStatusColor(booking.status as any);
  const paymentColor = getPaymentStatusColor(booking.payment_status as any);

  const courtName = booking.court?.name || 'N/A';
  const sportName = booking.court?.sport?.name || 'N/A';

  // Handle manual bookings (customer_name field) vs Spotts bookings (customer/user relationship)
  const oneTimeBooking = booking as OneTimeBooking;
  const customerName = oneTimeBooking.customer_name || booking.customer?.name || booking.user?.name || 'N/A';
  const customerPhone = oneTimeBooking.customer_phone || booking.customer?.phone || booking.user?.phone;
  const customerEmail = booking.customer?.email || booking.user?.email;

  const canRescheduleBooking = canReschedule(booking as any);
  const isCancelled = booking.status === 'cancelled';
  const isRefunded = booking.payment_status === 'refunded';
  const isRescheduled = !!(booking as OneTimeBooking).rescheduled_at;
  const cancelledByType = (booking as any).cancelled_by_type || 'unknown';

  // Check if this is a manual booking (can be deleted)
  const isManualBooking = !isRecurring && (booking as OneTimeBooking).source === 'manual';
  const canDelete = isManualBooking && onDelete;

  // Trusted reservation helpers
  const isTrustedReservation = !!(booking as any).is_trusted_reservation;
  const wasPaid = booking.payment_status === 'successful';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" maxHeight="max-h-[95vh] sm:max-h-[90vh]" animate={false}>
      {/* Header */}
      <div className={`sticky top-0 border-b px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10 ${
        isRefunded ? 'bg-purple-50 border-purple-100' :
        isCancelled ? 'bg-red-50 border-red-100' :
        isRescheduled ? 'bg-blue-50 border-blue-100' :
        'bg-green-50 border-green-100'
      }`}>
        <div className="flex items-center gap-2 sm:gap-3">
          {isRefunded ? <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" /> :
           isCancelled ? <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" /> :
           isRescheduled ? <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" /> :
           <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />}
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Booking Details</h2>
            <p className="text-xs sm:text-sm text-gray-700">{booking.booking_reference}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>
      </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Status Badges */}
          <div className="flex gap-2 flex-wrap">
            {/* Show REFUNDED badge only if payment_status is refunded, otherwise show booking status */}
            {isRefunded ? (
              <span className="px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-purple-100 text-purple-800 flex items-center gap-1">
                <RefreshCw className="w-3 h-3" />
                REFUNDED
              </span>
            ) : (
              <span className={`px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold ${statusColor}`}>
                {booking.status.replace('_', ' ').toUpperCase()}
              </span>
            )}

            {/* Show cancellation type badge for cancelled/refunded bookings */}
            {(isCancelled || isRefunded) && (
              <span className={`px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1 ${
                cancelledByType === 'vendor' || cancelledByType === 'admin'
                  ? 'bg-orange-100 text-orange-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                <XCircle className="w-3 h-3" />
                {cancelledByType === 'vendor' ? 'Venue Cancelled' :
                 cancelledByType === 'admin' ? 'Admin Cancelled' :
                 cancelledByType === 'customer' ? 'Customer Cancelled' :
                 'Cancelled'}
              </span>
            )}

            {!isRecurring && (booking as OneTimeBooking).source && (
              <span className={`px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold ${
                (booking as OneTimeBooking).source === 'manual'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-indigo-100 text-indigo-800'
              }`}>
                {(booking as OneTimeBooking).source === 'spotts' ? 'Mobile App' : 'Manual Booking'}
              </span>
            )}
            {!isRecurring && (booking as OneTimeBooking).created_by_name && (
              <span className="px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-purple-100 text-purple-800 flex items-center gap-1">
                <User className="w-3 h-3" />
                Added by {(booking as OneTimeBooking).created_by_name}
              </span>
            )}
            {isRescheduled && !isCancelled && !isRefunded && (
              <span className="px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-blue-100 text-blue-700 flex items-center gap-1">
                <RefreshCw className="w-3 h-3" />
                Rescheduled
              </span>
            )}
            {!isRecurring && (booking as OneTimeBooking).court?.allow_shared_booking && (
              <span className="px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-teal-100 text-teal-800 flex items-center gap-1">
                <Users className="w-3 h-3" />
                Shared Slot (Drop-in)
              </span>
            )}
            {isTrustedReservation && (
              <span className="px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-amber-100 text-amber-800 flex items-center gap-1">
                <Shield className="w-3 h-3" />
                {wasPaid ? 'Trusted Customer' : 'Reserved — Awaiting Payment'}
              </span>
            )}
          </div>

          {/* Customer Info */}
          <div className="bg-gray-50 rounded-2xl p-4 sm:p-5">
            <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              Customer Information
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Name</p>
                <p className="text-gray-900 font-semibold text-sm sm:text-base">{customerName}</p>
              </div>
              {customerEmail && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Email</p>
                  <a
                    href={`mailto:${customerEmail}`}
                    className="text-blue-600 hover:text-blue-700 font-semibold text-xs sm:text-sm break-all"
                  >
                    {customerEmail}
                  </a>
                </div>
              )}
              {customerPhone && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Phone</p>
                  <a
                    href={`tel:${customerPhone}`}
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1.5 font-semibold text-sm sm:text-base"
                  >
                    <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {customerPhone}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Booking Details */}
          <div className="bg-gray-50 rounded-2xl p-4 sm:p-5">
            <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              Booking Details
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Court</p>
                <p className="text-gray-900 font-semibold text-sm sm:text-base">{courtName}</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Sport</p>
                <p className="text-gray-900 font-semibold text-sm sm:text-base">{sportName}</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Date</p>
                <p className="text-gray-900 font-semibold text-sm sm:text-base">{formatDate(bookingDate)}</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Time</p>
                <p className="text-gray-900 font-semibold text-sm sm:text-base">
                  {formatTime(startTime)} - {formatTime(endTime)}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Duration</p>
                <p className="text-gray-900 font-semibold text-sm sm:text-base">{formatDurationFromTimes(startTime || '', endTime || '')}</p>
              </div>
              {!isRecurring && (booking as OneTimeBooking).number_of_players && (booking as OneTimeBooking).number_of_players! > 0 && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Players</p>
                  <p className="text-gray-900 font-semibold text-sm sm:text-base flex items-center gap-1">
                    <Users className="w-3.5 h-3.5 text-teal-600" />
                    {(booking as OneTimeBooking).number_of_players} player{(booking as OneTimeBooking).number_of_players! > 1 ? 's' : ''}
                  </p>
                </div>
              )}
              {(booking as OneTimeBooking).checked_in_at && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Checked In</p>
                  <p className="text-gray-900 font-semibold text-sm sm:text-base">{formatDateTimeString((booking as OneTimeBooking).checked_in_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Recurring Booking Info */}
          {isRecurring && (
            <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-4 sm:p-5">
              <h4 className="font-bold text-purple-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
                Recurring Booking Details
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <p className="text-xs sm:text-sm text-purple-600 mb-1">Frequency</p>
                  <p className="text-purple-900 font-semibold capitalize text-sm sm:text-base">{(booking as RecurringBooking).frequency.replace('_', ' ')}</p>
                </div>
                {(booking as RecurringBooking).total_sessions && (
                  <div>
                    <p className="text-xs sm:text-sm text-purple-600 mb-1">Total Sessions</p>
                    <p className="text-purple-900 font-semibold text-sm sm:text-base">{(booking as RecurringBooking).total_sessions}</p>
                  </div>
                )}
                {(booking as RecurringBooking).end_date && (
                  <div>
                    <p className="text-xs sm:text-sm text-purple-600 mb-1">End Date</p>
                    <p className="text-purple-900 font-semibold text-sm sm:text-base">{formatDate((booking as RecurringBooking).end_date)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs sm:text-sm text-purple-600 mb-1">Next Session</p>
                  <p className="text-purple-900 font-semibold text-sm sm:text-base">{formatDate(bookingDate)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Info */}
          <div className={`rounded-2xl p-4 sm:p-5 ${isTrustedReservation && !wasPaid ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
            <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
              <CreditCard className={`w-4 h-4 sm:w-5 sm:h-5 ${isTrustedReservation && !wasPaid ? 'text-amber-600' : 'text-green-600'}`} />
              Payment Information
            </h4>
            {/* Unpaid trusted reservation notice */}
            {isTrustedReservation && !wasPaid && (
              <div className="mb-3 p-3 bg-amber-100 rounded-xl text-amber-800 text-xs sm:text-sm font-medium flex items-start gap-2">
                <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {isCancelled
                    ? 'This reservation was cancelled before payment was made. No charge was collected.'
                    : 'Payment has not been received yet. This slot is reserved pending payment.'}
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">{isTrustedReservation && !wasPaid ? 'Booking Value' : 'Amount'}</p>
                <p className={`font-semibold text-sm sm:text-base ${isTrustedReservation && !wasPaid ? 'text-gray-500' : 'text-gray-900'}`}>
                  {formatCurrency(booking.final_price)}
                  {isTrustedReservation && !wasPaid && <span className="text-xs font-normal ml-1">(not yet paid)</span>}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-500 mb-1">Payment Status</p>
                <p className="font-semibold capitalize text-sm sm:text-base">
                  {isTrustedReservation && !wasPaid && isCancelled
                    ? <span className="text-red-600">Cancelled — Not Paid</span>
                    : isTrustedReservation && !wasPaid
                    ? <span className="text-amber-700">Awaiting Payment</span>
                    : <span className="text-gray-900">{(booking.payment_status || '').replace(/_/g, ' ')}</span>
                  }
                </p>
              </div>
              {booking.payment_reference && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Reference</p>
                  <p className="text-gray-900 font-mono text-xs sm:text-sm break-all">{booking.payment_reference}</p>
                </div>
              )}
              {booking.paid_at && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 mb-1">Paid At</p>
                  <p className="text-gray-900 font-semibold text-xs sm:text-sm">{formatDateTimeString(booking.paid_at)}</p>
                </div>
              )}
            </div>
            {/* Spotts-sponsored discount notice */}
            {!isRecurring && (booking as OneTimeBooking).discount_amount > 0 &&
              (booking as OneTimeBooking).discount_type === 'platform_promo' && (
              <div className="mt-3 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2">
                <span className="text-base">🎁</span>
                <p className="text-xs text-blue-700 font-medium">
                  Spotts sponsored a {formatCurrency((booking as OneTimeBooking).discount_amount)} discount on this booking.
                  Your earnings are not affected.
                </p>
              </div>
            )}
          </div>

          {/* Vendor Revenue Split — only shown when payment was actually received */}
          {booking.venue_share && wasPaid && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-4 sm:p-5">
              <h3 className="font-bold text-green-900 mb-3 sm:mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Venue Revenue
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="bg-white rounded-xl p-3 sm:p-4">
                  <p className="text-sm text-green-600 mb-1">Your Share</p>
                  <p className="text-green-900 font-black text-2xl sm:text-3xl">{formatCurrency(booking.venue_share)}</p>
                  <p className="text-xs text-gray-500 mt-1">Net amount after Paystack fee</p>
                </div>
                {booking.platform_commission && booking.platform_commission > 0 && (
                  <div className="bg-white rounded-xl p-3 sm:p-4">
                    <p className="text-sm text-gray-600 mb-1">Platform Fee</p>
                    <p className="text-gray-900 font-bold text-lg sm:text-xl">{formatCurrency(booking.platform_commission)}</p>
                  </div>
                )}
                {booking.gateway_fee && booking.gateway_fee > 0 && (
                  <div className="bg-white rounded-xl p-3 sm:p-4">
                    <p className="text-sm text-orange-600 mb-1">Paystack Gateway Fee</p>
                    <p className="text-orange-900 font-bold text-lg sm:text-xl">
                      {booking.gateway_fee_paid_by === 'customer' ? '+' : '-'}{formatCurrency(booking.gateway_fee)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {booking.gateway_fee_paid_by === 'venue'
                        ? 'Deducted from your share'
                        : booking.gateway_fee_paid_by === 'customer'
                        ? 'Added to customer total — not deducted from your share'
                        : 'Absorbed by Spotts — not deducted from your share'}
                    </p>
                  </div>
                )}
                <div className="pt-3 border-t-2 border-green-200 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-green-700 font-semibold">Booking Value</span>
                    <span className="text-green-900 font-bold text-lg sm:text-xl">{formatCurrency((booking as OneTimeBooking).original_price || booking.final_price)}</span>
                  </div>
                  {!isRecurring && (booking as OneTimeBooking).discount_amount > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-blue-600 text-sm font-medium flex items-center gap-1">
                        <span>🎁</span>
                        {(booking as OneTimeBooking).discount_type === 'platform_promo'
                          ? 'Spotts Promo (sponsored)'
                          : 'Promo Discount'}
                      </span>
                      <span className="text-blue-600 font-semibold text-sm">
                        -{formatCurrency((booking as OneTimeBooking).discount_amount)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Reschedule History */}
          {isRescheduled && (booking as OneTimeBooking).original_booking_date && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 sm:p-5">
              <h4 className="font-bold text-blue-900 mb-3 sm:mb-4 flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Reschedule History
                {(booking as OneTimeBooking).reschedule_count && (
                  <span className="text-xs font-normal text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                    {(booking as OneTimeBooking).reschedule_count}x
                  </span>
                )}
              </h4>
              <div className="space-y-3">
                {/* Show full reschedule history if available */}
                {(booking as OneTimeBooking).reschedule_history && (booking as OneTimeBooking).reschedule_history!.length > 0 ? (
                  (booking as OneTimeBooking).reschedule_history!.map((entry, idx) => (
                    <div key={entry.id} className="bg-white rounded-xl p-3 sm:p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-blue-600">
                          Reschedule #{idx + 1}
                          {entry.rescheduled_by_type === 'vendor' && (
                            <span className="ml-1 text-orange-600">
                              {entry.rescheduled_by != null && entry.rescheduled_by === currentUser?.id
                                ? '(by you)'
                                : entry.rescheduled_by_name
                                  ? `(by ${entry.rescheduled_by_name})`
                                  : '(by venue)'}
                            </span>
                          )}
                          {entry.rescheduled_by_type === 'customer' && (
                            <span className="ml-1 text-gray-500">(by customer)</span>
                          )}
                        </p>
                        {entry.rescheduled_at && (
                          <p className="text-xs text-gray-400">{formatDateTimeString(entry.rescheduled_at)}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm mb-2">
                        <span className="text-gray-500">
                          {entry.from_date ? formatDate(entry.from_date) : 'N/A'} {entry.from_start_time ? formatTime(entry.from_start_time) : ''}
                        </span>
                        <ArrowRight className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span className="text-gray-900 font-semibold">
                          {entry.to_date ? formatDate(entry.to_date) : 'N/A'} {entry.to_start_time ? formatTime(entry.to_start_time) : ''}
                        </span>
                      </div>

                      {/* Pricing change */}
                      {entry.price_difference != null && entry.price_difference !== 0 && (
                        <div className={`flex items-center gap-2 text-sm px-2 py-1 rounded-lg ${
                          entry.price_difference > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                        }`}>
                          <DollarSign className="w-3.5 h-3.5" />
                          <span className="font-medium">
                            {entry.from_price != null && `${formatCurrency(entry.from_price)} → ${formatCurrency(entry.to_price ?? 0)}`}
                          </span>
                          <span className="font-bold">
                            ({entry.price_difference > 0 ? '+' : ''}{formatCurrency(entry.price_difference)})
                          </span>
                          {entry.payment_status === 'successful' && (
                            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Paid</span>
                          )}
                        </div>
                      )}

                      {entry.reason && (
                        <p className="text-xs text-gray-500 mt-1">Reason: {entry.reason}</p>
                      )}
                    </div>
                  ))
                ) : (
                  /* Fallback: show simple original → new when no history array */
                  <div className="bg-white rounded-xl p-3 sm:p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs sm:text-sm text-blue-600 font-semibold mb-2">Original Booking</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-900 font-semibold">{formatDate((booking as OneTimeBooking).original_booking_date)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-900">
                              {(booking as OneTimeBooking).original_start_time && (booking as OneTimeBooking).original_end_time
                                ? `${formatTime((booking as OneTimeBooking).original_start_time)} - ${formatTime((booking as OneTimeBooking).original_end_time)}`
                                : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="hidden sm:flex items-center justify-center">
                        <ArrowRight className="w-8 h-8 text-blue-600" />
                      </div>
                      <div className="flex sm:hidden items-center justify-center py-2">
                        <ArrowRight className="w-6 h-6 text-blue-600 rotate-90" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-blue-600 font-semibold mb-2">New Booking</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-900 font-semibold">{formatDate(bookingDate)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-900">
                              {formatTime(startTime)} - {formatTime(endTime)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {(booking as OneTimeBooking).reschedule_reason && (
                  <div className="bg-blue-100 rounded-xl p-3">
                    <p className="text-blue-600 font-semibold text-xs mb-1">Reason:</p>
                    <p className="text-blue-800 text-sm">{(booking as OneTimeBooking).reschedule_reason}</p>
                  </div>
                )}

                {/* Total reschedule top-up summary */}
                {(() => {
                  const history = (booking as OneTimeBooking).reschedule_history ?? [];
                  const totalTopUp = history.reduce((sum, h) =>
                    sum + ((h.price_difference ?? 0) > 0 ? (h.price_difference ?? 0) : 0), 0);
                  if (totalTopUp > 0) {
                    return (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between">
                        <span className="text-sm font-semibold text-amber-800">Total Reschedule Top-up</span>
                        <span className="text-sm font-bold text-amber-900">{formatCurrency(totalTopUp)}</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}

          {/* Refund Information Section - Show for both cancelled and refunded bookings */}
          {(isCancelled || isRefunded) && (booking as any).refund_info && (
            <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-4 sm:p-5">
              <h4 className="font-bold text-purple-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
                Refund Information
              </h4>
              <div className="space-y-3 sm:space-y-4">
                {/* Refund Amount */}
                <div className="bg-white rounded-xl p-3 sm:p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <p className="text-xs sm:text-sm text-purple-600 mb-1">Customer Refund</p>
                      <p className="text-purple-900 font-black text-xl sm:text-2xl">
                        {formatCurrency((booking as any).refund_info?.refund_amount || (booking as any).refund_amount || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-purple-600 mb-1">Refund Status</p>
                      <span className={`inline-block px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold mt-1 ${
                        (booking as any).refund_info?.status === 'processed' || (booking as any).refund_status === 'processed'
                          ? 'bg-green-100 text-green-800'
                          : (booking as any).refund_info?.status === 'processing' || (booking as any).refund_status === 'processing'
                          ? 'bg-blue-100 text-blue-800'
                          : (booking as any).refund_info?.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {((booking as any).refund_info?.status || (booking as any).refund_status || 'Pending').replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Refund Timeline */}
                <div className="bg-white rounded-xl p-3 sm:p-4">
                  <p className="text-xs sm:text-sm text-purple-600 mb-2 sm:mb-3 font-semibold">Refund Timeline</p>
                  <div className="space-y-2 sm:space-y-3">
                    {(booking as any).refund_info?.requested_at && (
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-2 h-2 bg-purple-400 rounded-full flex-shrink-0"></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-600">Refund Requested</p>
                          <p className="text-xs sm:text-sm text-gray-900 font-semibold">
                            {formatDateTimeString((booking as any).refund_info.requested_at)}
                          </p>
                        </div>
                      </div>
                    )}
                    {(booking as any).refund_info?.processed_at && (
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-600">Refund Processed</p>
                          <p className="text-xs sm:text-sm text-gray-900 font-semibold">
                            {formatDateTimeString((booking as any).refund_info.processed_at)}
                          </p>
                        </div>
                      </div>
                    )}
                    {(booking as any).refund_info?.refund_reference && (
                      <div className="pt-2 border-t border-purple-100">
                        <p className="text-xs text-gray-600 mb-1">Refund Reference</p>
                        <p className="text-xs sm:text-sm text-gray-900 font-mono font-semibold break-all">
                          {(booking as any).refund_info.refund_reference}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Manual Booking Refund Information - Show when manual booking has refund_type but no refund_info (Spotts) */}
          {(isCancelled || isRefunded) && isManualBooking && (booking as any).refund_type && !(booking as any).refund_info && (
            <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-4 sm:p-5">
              <h4 className="font-bold text-purple-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
                Refund Information
              </h4>
              <div className="bg-white rounded-xl p-3 sm:p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-purple-600 mb-1">Refund Type</p>
                    <span className={`inline-block px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold ${
                      (booking as any).refund_type === 'full'
                        ? 'bg-green-100 text-green-800'
                        : (booking as any).refund_type === 'partial'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {(booking as any).refund_type === 'full' ? 'Full Refund' :
                       (booking as any).refund_type === 'partial' ? 'Partial Refund' :
                       'No Refund'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-purple-600 mb-1">Refund Amount</p>
                    <p className="text-purple-900 font-black text-xl sm:text-2xl">
                      {formatCurrency((booking as any).refund_amount || 0)}
                    </p>
                    {(booking as any).refund_type === 'partial' && (booking as any).final_price && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        of {formatCurrency((booking as any).final_price)} booking price
                      </p>
                    )}
                  </div>
                </div>
                {(booking as any).refund_processed_at && (
                  <div className="mt-3 pt-3 border-t border-purple-100">
                    <p className="text-xs text-gray-600">Refund Processed</p>
                    <p className="text-xs sm:text-sm text-gray-900 font-semibold">
                      {formatDateTimeString((booking as any).refund_processed_at)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cancellation Details */}
          {(isCancelled || isRefunded) && (
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 sm:p-5">
              <h4 className="font-bold text-red-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                Cancellation Details
              </h4>
              <div className="space-y-3 sm:space-y-4">
                {/* Cancellation Info */}
                <div className="bg-white rounded-xl p-3 sm:p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <p className="text-xs sm:text-sm text-red-600 mb-1">Cancelled By</p>
                      <p className="text-red-900 font-bold text-sm sm:text-base">
                        {cancelledByType === 'vendor' ? '🏢 Venue' :
                         cancelledByType === 'admin' ? '⚙️ Admin' :
                         cancelledByType === 'customer' ? '👤 Customer' :
                         '❓ Unknown'}
                      </p>
                      {cancelledByType === 'unknown' && (
                        <p className="text-xs text-gray-500 mt-1">Cancellation type not recorded</p>
                      )}
                    </div>
                    {(booking as any).cancelled_at && (
                      <div>
                        <p className="text-xs sm:text-sm text-red-600 mb-1">Cancelled On</p>
                        <p className="text-red-900 font-bold text-xs sm:text-sm">{formatDateTimeString((booking as any).cancelled_at)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Cancellation Reason */}
                {(booking as any).cancellation_reason && (
                  <div className="bg-white rounded-xl p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-red-600 mb-2 font-semibold">Reason for Cancellation</p>
                    <p className="text-red-900 text-sm sm:text-base">{(booking as any).cancellation_reason}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Per-booking history (activity.view) */}
          <BookingActivityTimeline
            venueId={booking.venue_id}
            bookingId={isRecurring ? undefined : booking.id}
            recurringBookingId={isRecurring ? booking.id : undefined}
          />
        </div>
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="sticky bottom-20 mx-4 sm:mx-6 bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-bold text-red-900 mb-1">Delete this booking?</h4>
                <p className="text-sm text-red-700 mb-4">
                  This action cannot be undone. The booking will be permanently removed from the system.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:bg-red-400 transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-4 py-2 bg-white text-gray-700 rounded-lg font-semibold hover:bg-gray-100 border border-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
          {canCheckIn(booking) && onCheckIn && (
            <button
              onClick={handleCheckIn}
              disabled={isCheckingIn || !allowCheckIn}
              title={allowCheckIn ? undefined : "You don't have permission to check in"}
              className="w-full sm:flex-1 bg-blue-600 text-white py-3.5 sm:py-3 rounded-xl font-bold text-sm sm:text-base hover:bg-blue-700 disabled:bg-gray-400 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              {isCheckingIn ? 'Checking In...' : 'Check In'}
            </button>
          )}

          {canRescheduleBooking && onReschedule && (
            <button
              onClick={() => allowReschedule && onReschedule(booking.id)}
              disabled={!allowReschedule}
              title={allowReschedule ? undefined : "You don't have permission to reschedule"}
              className="w-full sm:flex-1 bg-gray-100 text-gray-900 py-3 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-100"
            >
              <RefreshCw className="w-4 h-4" />
              Reschedule
            </button>
          )}

          {canCancel(booking) && onCancel && !isRefunded && (
            <button
              onClick={() => allowCancel && onCancel(booking.id)}
              disabled={!allowCancel}
              title={allowCancel ? undefined : "You don't have permission to cancel"}
              className="w-full sm:flex-1 bg-red-50 text-red-700 py-3 rounded-xl font-semibold text-sm hover:bg-red-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-50"
            >
              <XCircle className="w-4 h-4" />
              Cancel
            </button>
          )}

          {/* Delete button - only for manual bookings */}
          {canDelete && (
            <button
              onClick={() => allowDelete && setShowDeleteConfirm(true)}
              disabled={!allowDelete}
              title={allowDelete ? undefined : "You don't have permission to delete bookings"}
              className="w-full sm:w-auto bg-red-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-red-700 transition-colors flex items-center justify-center gap-2 px-6 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-600"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          )}

          <button
            onClick={onClose}
            className="w-full sm:w-auto bg-gray-200 text-gray-900 py-3 rounded-xl font-semibold text-sm hover:bg-gray-300 transition-colors px-8"
          >
            Close
          </button>
        </div>
    </Modal>
  );
}
